import {
  SD_849_ARCHIVE_MAX_EXTRACT_FILES,
  type EmailArchiveMember,
} from '@erp/rpc';

/**
 * The zip picker's rules — pure, so what may be ticked and what the select-all
 * box shows are testable without a DOM.
 *
 * Nothing starts ticked: which files answer the claim is the reviewer's call,
 * and a pre-ticked list read as already answered is how a zip's unrelated files
 * would end up on the email.
 */

/** A file that can still be added: readable, and not on the email already. */
export function isPickable(member: EmailArchiveMember): boolean {
  return member.unavailableReason == null && member.attachmentId == null;
}

export function pickablePaths(
  members: readonly EmailArchiveMember[],
): string[] {
  return members.flatMap((m) => (isPickable(m) ? [m.path] : []));
}

/** The selection with one file ticked or unticked. */
export function togglePick(
  selected: ReadonlySet<string>,
  path: string,
  picked: boolean,
): Set<string> {
  const next = new Set(selected);
  if (picked) next.add(path);
  else next.delete(path);
  return next;
}

/** The select-all box: every pickable file ticked, some, or none. */
export function selectAllState(
  members: readonly EmailArchiveMember[],
  selected: ReadonlySet<string>,
): boolean | 'indeterminate' {
  const pickable = pickablePaths(members);
  const ticked = pickable.filter((p) => selected.has(p)).length;
  if (ticked === 0) return false;
  return ticked === pickable.length ? true : 'indeterminate';
}

/** Why the picked files cannot be added yet, or null when they can. */
export function pickBlocker(
  selected: ReadonlySet<string>,
): 'none_picked' | 'too_many' | null {
  if (selected.size === 0) return 'none_picked';
  if (selected.size > SD_849_ARCHIVE_MAX_EXTRACT_FILES) return 'too_many';
  return null;
}
